import type { ProfileResponse } from "@/backend-sdk/client/types.gen";
import type { TeamScope, TeamScopeAccount } from "@/services/team.service";

import { ehDonoDasContas } from "@/lib/account-access-context";

/**
 * O que o app decide a partir do **escopo cedido** — `GET /api/v1/teams/my-scope`.
 *
 * Duas pessoas leem essa resposta, e as duas navegam do mesmo jeito:
 *
 * - o **funcionário da agência**, que alcança as lojas dos times de que participa. Um time
 *   preencheu o buraco entre dois extremos: a agência alcançava **todas** as lojas, quem é
 *   da loja alcançava **uma**, e o funcionário — que só tem vínculo na conta principal —
 *   não alcançava **nenhuma**;
 * - a **credencial cedida ao cliente**, criada em "Usuários" com o tipo *Cliente*, que
 *   alcança as lojas que a agência marcou para ela. Ela recebe `teams: []` e as lojas em
 *   `accounts` — por isso o que decide a navegação é a lista de **lojas**, e não a de
 *   times.
 *
 * <M-248> Os dois alcances **eram** só de leitura, e deixaram de ser em <D-061>: o que o
 * servidor recusa é a escrita de quem é `viewer`, e não a de quem chegou por estas trilhas.
 * Quem administra a conta pode promover a pessoa a `editor`, e aí ela escreve na loja que
 * acompanha. É por isso que a navegação e o modo somente leitura, que este arquivo tratava
 * como um predicado só, passaram a ser dois — `navegaPorEscopoCedido` e `escreveNaLoja`.
 *
 * Este arquivo concentra as perguntas que isso cria, para que nem o contexto de
 * autenticação nem o de contas precisem repeti-las: quem administra times, quem navega por
 * escopo cedido, e qual loja vale como ativa para quem navega assim.
 *
 * A régua de "qual loja vale" é a mesma de `contaPadraoDoDono` (o lojista multi-conta de
 * M-214) e a de `contaPadraoDoConvidado` que existiu aqui antes: toda tela lê
 * `active_customer_uuid` como tenant e algumas quebram sem ele, então nunca pode faltar uma
 * escolhida. O filtro é o mesmo — loja sem `customer_uuid` não tem a quem pedir dados.
 */

/**
 * Quem vê as telas de gestão de times.
 *
 * <M-250> Quem responde é **`can_manage_teams`**, que `GET /users/me` publica: ele é
 * `TeamService.agenciaDoOperador` chamado — o assinante, ou quem tem `access_level = admin`
 * **na matriz** da agência —, e não uma paráfrase dele.
 *
 * A régua anterior comparava `accounts[].role` com `"admin"` e **nunca era verdadeira**:
 * `role` é `roles.name` (`Administrador`, `Proprietário`, `Visualizador`), o espelho que
 * <D-061> aposentou. Na prática, só o assinante via o item; o funcionário que a agência
 * promoveu a administrador não via — e ele é justamente a pessoa a quem a delegação existe
 * para servir.
 *
 * E trocar o campo pelo eixo vivo não resolveria. `access_level = 'admin'` é também o do
 * **dono de uma loja**, que administra a loja dele e não a agência: `agenciaDoOperador` lhe
 * responde `403`, porque o que separa os dois é qual conta é a matriz — e o perfil não diz
 * qual é. Era escolher entre esconder de quem pode e oferecer a quem não pode.
 *
 * O campo é `optional` no contrato pela régua de D-039/M-222, então o tipo gerado o traz
 * como `boolean | undefined`, e `undefined` **não** é `false`: é "este servidor não publica
 * o campo". Tratá-lo como `false` seria uma regressão, e não um default fechado — a régua
 * antiga acertava o assinante por `ehDonoDasContas`, e ele perderia o item de menu se o
 * `frontend` subisse antes da API. Por isso o ramo de compatibilidade devolve exatamente a
 * única metade da régua antiga que chegava a ser verdadeira. Achado de code review.
 */
export const podeGerirTimes = (
  perfil: ProfileResponse | null | undefined,
): boolean => {
  if (!perfil) return false;
  if (perfil.can_manage_teams !== undefined) return perfil.can_manage_teams;
  return ehDonoDasContas(perfil.accounts);
};

/**
 * Só as lojas do escopo que têm tenant — as outras não abrem tela nenhuma.
 *
 * Vale para as duas origens: as lojas dos times e as cedidas à credencial de cliente
 * chegam na mesma lista `accounts`, com o mesmo `customer_uuid`.
 *
 * <D-062> O filtro deixou de tirar alguém: o `customer_uuid` passou a ser o próprio
 * `account_id` — a loja é dona dos próprios dados —, e o campo sai em toda linha. Ele era o
 * `users.id` do dono, e faltava na loja sem vínculo nenhum em `account_users`, que é
 * exatamente o estado que a criação de conta produz agora. Fica como guarda de payload
 * malformado, e não como recorte de produto.
 */
export const lojasAbriveisDoMembro = (
  escopo: TeamScope | null,
): TeamScopeAccount[] =>
  (escopo?.accounts ?? []).filter((loja) => !!loja.customer_uuid);

/**
 * O usuário é membro de algum **time**?
 *
 * Continua sendo sobre times, e só sobre eles: é o que decide a face da tela de times.
 * Quem navega pelo escopo sem estar em time nenhum — a credencial de cliente — responde
 * `false` aqui e `true` em `navegaPorEscopoCedido`, que é o predicado da navegação.
 *
 * `my-scope` responde `200` para todo autenticado, e o assinante recebe `teams: []` porque
 * não é membro de nada. A conferência de `ehDonoDasContas` é o cinto: um assinante que por
 * algum motivo tenha vínculo na própria conta principal e apareça num time continua sendo
 * tratado como dono, e não perde o seletor de contas dele.
 */
export const ehMembroDeTime = (
  perfil: ProfileResponse | null | undefined,
  escopo: TeamScope | null,
): boolean => {
  if (ehDonoDasContas(perfil?.accounts)) return false;
  return (escopo?.teams.length ?? 0) > 0;
};

/**
 * O usuário **navega pelo escopo cedido**: as lojas que ele abre vêm de `my-scope`.
 *
 * Repare que ele **não** pergunta por times: a credencial de cliente não participa de
 * nenhum e mesmo assim navega pelas lojas que lhe foram cedidas. O que importa, para
 * escolher de onde vêm as lojas da tela, é haver loja abrível no escopo.
 *
 * É mais estreito do que `ehMembroDeTime` de propósito. Um time sem loja nenhuma é estado
 * legítimo — a agência costuma criar o time antes de saber quais lojas entram nele —, e o
 * membro dele continua alcançando o que alcançava antes (a conta principal, onde tem
 * vínculo). Trocar o contexto de contas dele por uma lista vazia deixaria a tela sem
 * tenant, que é pior do que não mudar nada.
 *
 * <M-248> Ele **não** decide mais o modo somente leitura, e é essa separação que o item
 * fez: até <D-061> os dois eram a mesma coisa, porque a trilha recusava toda escrita, e o
 * predicado da navegação servia de predicado da escrita de graça. Hoje quem escreve é quem
 * tem nível para isso, e a pergunta "de onde vêm as lojas desta tela?" deixou de responder
 * "esta pessoa pode mudar alguma coisa?".
 *
 * O que ele continua decidindo, além da origem das lojas, é o que **não** é sobre nível:
 * quem navega assim não é dono de conta nenhuma, então "Contas da Agência" não descreve
 * tela que ele possa abrir — `GET /api/v1/accounts` lhe responde
 * `403 Usuário não é um assinante`, seja ele `viewer` ou `editor`.
 */
export const navegaPorEscopoCedido = (
  perfil: ProfileResponse | null | undefined,
  escopo: TeamScope | null,
): boolean =>
  !ehDonoDasContas(perfil?.accounts) &&
  lojasAbriveisDoMembro(escopo).length > 0;

/**
 * A loja aceita escrita de quem está olhando?
 *
 * <M-248> Quem responde é o **servidor**: `can_write` é
 * `TenantGuardService.alcanceDe` avaliado para aquela loja, publicado em
 * `GET /api/v1/teams/my-scope`. Reconstruir a resposta aqui não daria certo — o vínculo que
 * decide o alcance do funcionário mora na **matriz** da agência, e a lista `accounts` do
 * perfil traz as contas em que ele tem vínculo, não as lojas que o time lhe dá.
 *
 * A pergunta é por **loja**, e não por pessoa, porque é assim que a barreira responde: um
 * vínculo direto de leitura numa loja específica vence o nível que o time concede.
 *
 * Loja desconhecida — escopo ainda não carregado, loja fora dele, ou um servidor anterior
 * ao campo — responde `false`. A dúvida cai do lado que **esconde** o botão: oferecer o que
 * a API vai recusar é o defeito que esta camada existe para evitar, e o contrário só custa
 * um item de menu a menos até a resposta chegar.
 */
export const escreveNaLoja = (
  escopo: TeamScope | null,
  lojaId: string | null | undefined,
): boolean => {
  if (!lojaId) return false;
  return (
    lojasAbriveisDoMembro(escopo).find((loja) => loja.account_id === lojaId)
      ?.can_write === true
  );
};

/**
 * A loja que vale como ativa: a última escolhida, se ainda está no escopo, ou a primeira.
 *
 * Devolve `null` quando o membro não alcança loja abrível nenhuma — estado real (time sem
 * lojas, ou lojas sem vínculo em `account_users`), e que a tela precisa distinguir de
 * "ainda carregando".
 */
export const lojaPadraoDoMembro = (
  escopo: TeamScope | null,
  ultimaEscolhidaId: string | null,
): TeamScopeAccount | null => {
  const lojas = lojasAbriveisDoMembro(escopo);
  return (
    lojas.find((loja) => loja.account_id === ultimaEscolhidaId) ??
    lojas[0] ??
    null
  );
};
